import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';
import type { ApiLine } from './lines';

export interface Scan {
  id: number;
  direction: 'outgoing' | 'incoming';
  fileName: string;
  source: string;
  warnings: string[];
  prefill: {
    series?: string; number?: string; issueDate?: string; dueDate?: string;
    party: { name: string; companyCode?: string; vatCode?: string; address?: string; email?: string; iban?: string; bankName?: string; swift?: string };
    /** The invoice's currency when it is not the euro. */
    currency?: string;
    lines: ApiLine[];
    printedTotal?: number;
  };
  /** The inbox document it was opened from (e-mailed or photographed), if any. */
  inboxId?: number | null;
  /** Whom the invoice was made out to (received invoices): the company, someone else — maybe another company of this person — or nobody. */
  buyer?: { status: 'ours' | 'other' | 'none'; name: string; moveTo: { id: number; name: string } | null } | null;
  company?: string;
}

/** The pending upload the form was opened for (kept on the server until saved or for a day). */
export function useScan(id: number | undefined): Scan | undefined {
  const scan = useQuery({ queryKey: ['scan', id], enabled: Boolean(id), staleTime: Infinity, queryFn: () => api.get<{ scan: Scan }>(`api/v1/scans/${id}`).then((r) => r.scan) });
  return scan.data;
}

const UPLOAD_ERRORS: Record<string, string> = {
  file_type_not_allowed: 'Neleistinas failo tipas. Įkelkite PDF, nuotrauką arba Word (.docx) failą.',
  old_word_format: 'Senų Word .doc failų perskaityti negalima. Išsaugokite failą kaip .docx arba PDF.',
  unreadable_file: 'Failo perskaityti nepavyko. Įkelkite aiškesnį PDF, nuotrauką arba Word failą.',
  file_too_large: 'Failas per didelis (daugiausia 12 MB).',
  reader_unavailable: 'Nuskaitymo paslauga šiuo metu nepasiekiama. Bandykite vėliau arba užpildykite formą rankiniu būdu.',
};

/**
 * "Upload an existing invoice": one file is read and opens the form filled with it. Several received invoices
 * (`batch`) are read one after another into the inbox (Gauta el. paštu), to be checked and saved there.
 */
export function ScanUpload({ direction, batch = false }: { direction: 'outgoing' | 'incoming'; batch?: boolean }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; failed: string[] } | null>(null);
  const many = batch && direction === 'incoming';
  const send = async (url: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(url, { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new ApiError(response.status, data?.error?.code ?? 'server_error');
    return data;
  };
  const upload = useMutation({
    mutationFn: async (file: File) => ((await send(`api/v1/scans?direction=${direction}`, file)) as { scan: Scan }).scan,
    onSuccess: async (scan) => {
      queryClient.setQueryData(['scan', scan.id], scan);
      await navigate(direction === 'incoming' ? { to: '/incoming/new', search: { scan: scan.id } } : { to: '/outgoing/new', search: { scan: scan.id } });
    },
  });
  // Several files: each into the inbox, one at a time (reading takes a while); then the inbox to check them.
  const uploadAll = useMutation({
    mutationFn: async (list: File[]) => {
      const failed: string[] = [];
      setProgress({ done: 0, total: list.length, failed });
      for (const [i, file] of list.entries()) {
        try { await send('api/v1/inbox/upload', file); } catch (e) {
          if (e instanceof ApiError && (e.status === 401 || e.status === 403)) throw e;
          failed.push(file.name);
        }
        setProgress({ done: i + 1, total: list.length, failed: [...failed] });
      }
      return failed;
    },
    onSuccess: async (failed) => {
      await queryClient.invalidateQueries({ queryKey: ['inbox'] });
      if (!failed.length) await navigate({ to: '/invoices', search: { tab: 'inbox' } });
    },
  });
  const pick = (list: FileList | null | undefined) => {
    const chosen = [...(list ?? [])];
    setFiles(many ? chosen.slice(0, 50) : chosen.slice(0, 1));
    setProgress(null); upload.reset(); uploadAll.reset();
  };
  const busy = upload.isPending || uploadAll.isPending;
  const error = upload.error ?? uploadAll.error;
  const message = error instanceof ApiError ? UPLOAD_ERRORS[error.code] : undefined;
  const label = files.length > 1 ? t('Pasirinkta failų: {n}', { n: files.length }) : files[0]?.name ?? t('Failas nepasirinktas');
  return (
    <section className="panel-card scan-card">
      <p className="eyebrow">{t('Nuskaitymas')}</p>
      <h2>{t('Įkelti esamą sąskaitą')}</h2>
      <p className="muted">{many ? t('PDF, nuotrauka arba Word (.docx). Vienas failas – duomenys bus užpildyti formoje; keli failai – visi bus nuskaityti į „Gauta el. paštu“, ten patikrinkite ir išsaugokite.')
        : t('PDF, nuotrauka arba Word (.docx). Duomenys bus užpildyti formoje — patikrinkite ir išsaugokite.')}</p>
      {error ? <Alert kind="error">{message ? t(message) : errorText(t, error)}</Alert> : null}
      {progress && progress.done === progress.total && progress.failed.length ? <Alert kind="error">{t('Nepavyko nuskaityti: {list}. Kiti failai – skiltyje „Gauta el. paštu“.', { list: progress.failed.join(', ') })}</Alert> : null}
      <form onSubmit={(e) => { e.preventDefault(); if (files.length > 1) uploadAll.mutate(files); else if (files[0]) upload.mutate(files[0]); }}>
        <label className={`drop-zone${dragging ? ' is-dragging' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}>
          <input ref={input} className="visually-hidden" type="file" multiple={many} accept="application/pdf,image/jpeg,image/png,image/webp,.docx" onChange={(e) => pick(e.target.files)} />
          <span className="drop-icon">⇧</span><strong>{many ? t('Pasirinkite arba nutempkite failus') : t('Pasirinkite failą')}</strong><em>{label}</em>
        </label>
        {progress && uploadAll.isPending ? <p className="muted">{t('Nuskaitoma {done} iš {total}…', { done: progress.done + 1 > progress.total ? progress.total : progress.done + 1, total: progress.total })}</p> : null}
        <button className="button button--primary" type="submit" disabled={busy || !files.length}>
          {busy ? t('Skaitoma…') : files.length > 1 ? t('Nuskaityti visus ({n})', { n: files.length }) : t('Nuskaityti')}</button>
      </form>
      {direction === 'incoming' ? <PhoneScan /> : null}
    </section>
  );
}

/** Shown above a form that was filled from an upload: what was read and what to check. */
export function ScanNotice({ scan }: { scan: Scan }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const buyer = scan.direction === 'incoming' ? scan.buyer : null;
  // Photographed or e-mailed to the wrong company of the person: moved to the right one's documents.
  const move = useMutation({
    mutationFn: () => api.post(`api/v1/inbox/${scan.inboxId}/move`, { companyId: buyer!.moveTo!.id }),
    onSuccess: async () => {
      await api.delete(`api/v1/scans/${scan.id}`).catch(() => undefined);
      await queryClient.invalidateQueries({ queryKey: ['inbox'] });
      await navigate({ to: '/incoming' });
    },
  });
  const discard = async () => {
    await api.delete(`api/v1/scans/${scan.id}`).catch(() => undefined);
    await navigate({ to: scan.direction === 'incoming' ? '/incoming' : '/outgoing' });
  };
  return (
    <section className="panel-card scan-notice">
      <p className="eyebrow">{t('Nuskaityta iš failo')}</p>
      <h2>{scan.fileName}</h2>
      <p className="muted">{t('Patikrinkite laukus ir išsaugokite. Originalus failas bus pridėtas prie sąskaitos.')}</p>
      {scan.source === 'ocr' || scan.warnings.length ? (
        <ul className="scan-warnings">
          {scan.source === 'ocr' ? <li>{t('Perskaityta iš nuotraukos — patikrinkite kiekvieną lauką.')}</li> : null}
          {scan.warnings.filter((w) => !w.startsWith('Read from an image')).map((w) => <li key={w}>{t(w)}</li>)}
        </ul>
      ) : null}
      {buyer?.status === 'other' ? (
        <Alert kind="error">
          {buyer.name
            ? t('Pirkėjas šioje sąskaitoje – {buyer}, ne {company}. Tai ne įmonės išlaida: paprašykite tiekėjo išrašyti sąskaitą įmonei.', { buyer: buyer.name, company: scan.company ?? '' })
            : t('Šioje sąskaitoje nurodytas kitas pirkėjas, ne {company}. Paprašykite tiekėjo išrašyti sąskaitą įmonei.', { company: scan.company ?? '' })}
          {buyer.moveTo && scan.inboxId ? <> <button type="button" className="text-link" onClick={() => move.mutate()} disabled={move.isPending}>{t('Perkelti į {company}', { company: buyer.moveTo.name })}</button></> : null}
        </Alert>
      ) : null}
      {move.isError ? <Alert kind="error">{errorText(t, move.error)}</Alert> : null}
      {buyer?.status === 'none' ? <ul className="scan-warnings"><li>{t('Sąskaitoje nenurodytas pirkėjas (pvz., kasos čekis). Be įmonės duomenų PVM atskaityti negalima.')}</li></ul> : null}
      <button type="button" className="text-link" onClick={discard}>{t('Atšaukti įkėlimą')}</button>
    </section>
  );
}

interface ScanLink { id: number; url: string; expiresAt: string; sent: number; pushEnabled: boolean }

/**
 * "Nuskaityti telefonu": a QR (or a notification to the person's phone) opens the app's scanner; the document
 * lands in the company's documents and, once read, this page opens it in the form by itself.
 */
export function PhoneScan() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [link, setLink] = useState<ScanLink | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  // Sent to the phone: the QR only when asked for (the notification did not come, another phone).
  const [showQr, setShowQr] = useState(false);
  const create = useMutation({
    mutationFn: (send: boolean) => api.post<{ link: ScanLink }>('api/v1/scan-links', { send }).then((r) => r.link),
    onSuccess: (l) => { setLink(l); setShowQr(!l.sent); },
  });
  useEffect(() => {
    if (!link) { setQr(null); return; }
    void QRCode.toDataURL(link.url, { margin: 1, width: 220, errorCorrectionLevel: 'M' }).then(setQr);
  }, [link]);
  const status = useQuery({
    queryKey: ['scan-link', link?.id], enabled: Boolean(link),
    queryFn: () => api.get<{ state: 'waiting' | 'reading' | 'ready' | 'expired'; documentId: number | null }>(`api/v1/scan-links/${link!.id}`),
    refetchInterval: (q) => (q.state.data?.state === 'ready' || q.state.data?.state === 'expired' ? false : 2000),
  });
  const open = useMutation({
    mutationFn: (id: number) => api.post<{ scan: Scan }>(`api/v1/inbox/${id}/open`),
    onSuccess: async (r) => {
      queryClient.setQueryData(['scan', r.scan.id], r.scan);
      await queryClient.invalidateQueries({ queryKey: ['inbox'] });
      await navigate({ to: '/incoming/new', search: { scan: r.scan.id } });
    },
  });
  const state = status.data?.state;
  const ready = state === 'ready' ? status.data?.documentId : null;
  useEffect(() => { if (ready && !open.isPending && !open.isSuccess) open.mutate(ready); }, [ready, open]);
  if (!link) {
    return (
      <div className="phone-scan">
        <button className="button button--ghost" type="button" disabled={create.isPending} onClick={() => create.mutate(true)}>{t('Nuskaityti telefonu')}</button>
        {create.error ? <Alert kind="error">{errorText(t, create.error)}</Alert> : null}
      </div>
    );
  }
  return (
    <div className="phone-scan phone-scan--open">
      <p className="eyebrow">{t('Nuskaityti telefonu')}</p>
      {state === 'expired' ? <Alert kind="error">{t('Kodo galiojimas baigėsi.')}</Alert>
        : state === 'reading' || state === 'ready' ? <Alert kind="success">{t('Dokumentas gautas iš telefono. Skaitoma…')}</Alert>
        : (
          <>
            {link.sent ? <Alert kind="success">{t('Pranešimas išsiųstas į jūsų telefoną: bakstelėkite jį ir nuskaitykite dokumentą. Jis atsiras čia.')}</Alert> : null}
            {showQr && qr ? <img className="phone-scan__qr" src={qr} width={220} height={220} alt={t('QR kodas telefonui')} /> : null}
            {showQr ? <p className="muted">{link.sent ? t('Arba nuskaitykite QR kodą telefono kamera. Kodas galioja 10 minučių.')
              : t('Nuskaitykite QR kodą telefono kamera: atsidarys FakturaSmart Pro skaitytuvas. Kodas galioja 10 minučių; dokumentas atsiras čia.')}</p> : null}
            {!link.sent && link.pushEnabled ? <p className="muted">{t('Kad dokumentą galėtumėte siųsti be QR kodo, prisijunkite programėlėje tuo pačiu naudotoju ir įjunkite pranešimus.')}</p> : null}
          </>
        )}
      {open.error ? <Alert kind="error">{errorText(t, open.error)}</Alert> : null}
      <div className="form-actions">
        {state === 'expired' ? <button className="button button--ghost" type="button" onClick={() => create.mutate(true)}>{t('Naujas kodas')}</button> : null}
        {state !== 'reading' && state !== 'ready' && link.sent && !showQr ? (
          <button className="button button--ghost" type="button" onClick={() => setShowQr(true)}>{t('Rodyti QR kodą')}</button>
        ) : null}
        {state !== 'reading' && state !== 'ready' && link.sent ? (
          <button className="button button--ghost" type="button" disabled={create.isPending} onClick={() => create.mutate(true)}>{t('Siųsti dar kartą')}</button>
        ) : null}
        <button className="button button--ghost" type="button" onClick={() => { setLink(null); create.reset(); }}>{t('Uždaryti')}</button>
      </div>
    </div>
  );
}
